import { formatDayHeader, type IsoDate } from "@/lib/time/dates";

/**
 * A task as an agent or screen reader names it: the task, then its project. Task names repeat
 * across projects ("Product Management" under five SOWs), so the project keeps every star and
 * hours box on the page uniquely named (UI standard 12).
 */
export function taskLabel(taskName: string, projectName: string | null | undefined): string {
  const project = projectName?.trim();
  return project ? `${taskName} (${project})` : taskName;
}

/**
 * The line under a task in a picker: its account, then its project ("Crye · CP - App Dev"), so
 * same-named projects under different clients can be told apart. An account missing, or the same
 * as the project, is left out.
 */
export function projectLine(
  projectName: string | null | undefined,
  accountName: string | null | undefined,
): string {
  const project = projectName?.trim() ?? "";
  const account = accountName?.trim() ?? "";
  if (!account || account.toLowerCase() === project.toLowerCase()) return project;
  return project ? `${account} · ${project}` : account;
}

/** The accessible name of one hours box in the week grid. */
export function cellLabel(
  task: { taskName: string; projectName: string | null | undefined },
  date: IsoDate,
): string {
  return `${taskLabel(task.taskName, task.projectName)}, ${formatDayHeader(date)}, hours`;
}

/**
 * The grid's delete confirm description ("Build (Acme), Wed 14"), read by screen readers and
 * agents only: the visible title names the hours and day, this names the task (rule 12).
 */
export function deleteCellDescription(
  task: { taskName: string; projectName: string | null | undefined },
  date: IsoDate,
): string {
  return `${taskLabel(task.taskName, task.projectName)}, ${formatDayHeader(date)}`;
}

/** The favorite star's accessible name. */
export function favoriteLabel(
  task: { taskName: string; projectName: string | null | undefined },
  isFavorite: boolean,
): string {
  const name = taskLabel(task.taskName, task.projectName);
  return isFavorite ? `Remove ${name} from favorites` : `Add ${name} to favorites`;
}

/** The Copy link button's accessible name (a task link, src/lib/time/task-link.ts). */
export function copyLinkLabel(task: {
  taskName: string;
  projectName: string | null | undefined;
}): string {
  return `Copy link to ${taskLabel(task.taskName, task.projectName)}`;
}

/**
 * An entry's action button name: "Edit Build (Acme), Tue 13, 2 hrs". Task, project, day and
 * hours; identical entries add a suffix from `entryLabelSuffixes`.
 */
export function entryActionLabel(
  verb: "Copy" | "Edit" | "Delete",
  entry: { taskName: string; projectName: string | null | undefined; date: IsoDate; hours: number },
  /** From `entryLabelSuffixes`, when the list can hold identical entries. */
  suffix = "",
): string {
  const hrs = `${entry.hours} ${entry.hours === 1 ? "hr" : "hrs"}`;
  return `${verb} ${taskLabel(entry.taskName, entry.projectName)}, ${formatDayHeader(entry.date)}, ${hrs}${suffix}`;
}

type LabelledEntry = {
  id: string;
  taskName: string;
  projectName: string | null | undefined;
  date: IsoDate;
  hours: number;
};

/**
 * Two entries with the same task, day and hours would share their Copy / Edit / Delete names (a
 * month of real data has them). Each such entry gets " (1 of 2)", " (2 of 2)" in the order of
 * `entries`, which the caller passes in display order (top row first), so "(1 of 2)" is the upper
 * of the two; every other entry "".
 */
export function entryLabelSuffixes(entries: LabelledEntry[]): Map<string, string> {
  const groups = new Map<string, string[]>();
  for (const e of entries) {
    const key = entryActionLabel("Copy", e);
    groups.set(key, [...(groups.get(key) ?? []), e.id]);
  }
  const out = new Map<string, string>();
  for (const ids of groups.values()) {
    ids.forEach((id, i) => {
      out.set(id, ids.length > 1 ? ` (${i + 1} of ${ids.length})` : "");
    });
  }
  return out;
}
